import {
  buildOptimisticLikeState,
  reconcileLikeStateFromResponse,
} from "../../../utils/likeInteraction";
/**
 * Normalizes club feed / single-post API payloads for UI (snake_case, JSON strings, unwrap).
 */

function parseAttachments(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return [];
    try {
      const p = JSON.parse(s);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

function asBool(v) {
  if (v === true || v === false) return v;
  if (v === "t" || v === "true" || v === "1" || v === 1) return true;
  if (v === "f" || v === "false" || v === "0" || v === 0) return false;
  return false;
}

/**
 * Club comment `content` is stored as plain text or JSON string `{ text, attachments }`.
 * node-pg may return JSON/JSONB columns as objects; normalize to the same string shape the UI parses.
 */
function coerceClubCommentContentToString(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string") return raw;
  if (typeof raw === "object") {
    const t = raw?.text;
    const att = raw?.attachments;
    if (t != null || Array.isArray(att)) {
      try {
        return JSON.stringify({
          text: t != null ? String(t) : "",
          attachments: Array.isArray(att) ? att : [],
        });
      } catch {
        return "";
      }
    }
    try {
      return JSON.stringify(raw);
    } catch {
      return "";
    }
  }
  return String(raw);
}

/**
 * Unwrap { post } or accept a raw post row; return a plain object for React state.
 */
export function extractClubPostFromResponse(data) {
  if (data == null || typeof data !== "object") return null;
  let raw = data.post ?? data.data?.post ?? null;
  if (!raw && typeof data.id !== "undefined") {
    const looksLikeRow =
      typeof data.user_id !== "undefined" ||
      typeof data.club_id !== "undefined" ||
      data.attachments != null ||
      typeof data.content !== "undefined" ||
      typeof data.image_url !== "undefined";
    if (looksLikeRow) raw = data;
  }
  if (!raw || typeof raw !== "object") return null;
  return normalizeClubFeedPostRow(raw);
}

/**
 * Single row shape from listClubPosts / getClubPostById (after unwrap).
 */
export function normalizeClubFeedPostRow(raw) {
  if (!raw || typeof raw !== "object") return null;

  const username =
    raw.username ??
    raw.user_name ??
    raw.userName ??
    raw.display_name ??
    raw.displayName ??
    null;

  const profile_picture =
    raw.profile_picture ?? raw.profilePicture ?? raw.avatar_url ?? raw.avatarUrl ?? null;

  const content =
    raw.content != null && raw.content !== ""
      ? String(raw.content)
      : raw.body != null
        ? String(raw.body)
        : raw.text != null
          ? String(raw.text)
          : "";

  const image_url = raw.image_url ?? raw.imageUrl ?? null;

  const attachments = parseAttachments(raw.attachments);

  return {
    ...raw,
    id: raw.id != null ? raw.id : raw.post_id,
    club_id: raw.club_id ?? raw.clubId,
    user_id: raw.user_id ?? raw.userId,
    content,
    username,
    profile_picture,
    image_url,
    attachments,
    like_count: Number(raw.like_count ?? raw.likeCount ?? 0) || 0,
    comment_count: Number(raw.comment_count ?? raw.commentCount ?? 0) || 0,
    liked_by_viewer: asBool(raw.liked_by_viewer ?? raw.likedByViewer),
    created_at: raw.created_at ?? raw.createdAt,
    updated_at: raw.updated_at ?? raw.updatedAt,
    artwork_id: raw.artwork_id ?? raw.artworkId,
    artwork_title: raw.artwork_title ?? raw.artworkTitle,
    artwork_image_url: raw.artwork_image_url ?? raw.artworkImageUrl,
    artwork_description: raw.artwork_description ?? raw.artworkDescription,
    artwork_artist_id: raw.artwork_artist_id ?? raw.artworkArtistId,
    feed_row_source:
      raw.feed_row_source != null && String(raw.feed_row_source).trim() !== ""
        ? String(raw.feed_row_source).trim()
        : undefined,
  };
}

export function getImageAttachmentsFromPost(post) {
  const list = Array.isArray(post?.attachments) ? post.attachments : [];
  const images = list.filter((a) => String(a?.kind || "").toLowerCase() === "image" && a?.url);
  return [...images].sort((a, b) => {
    const sa = Number(a.sort_order ?? a.sortOrder) || 0;
    const sb = Number(b.sort_order ?? b.sortOrder) || 0;
    if (sa !== sb) return sa - sb;
    const ia = Number(a.id);
    const ib = Number(b.id);
    if (Number.isFinite(ia) && Number.isFinite(ib)) return ia - ib;
    return 0;
  });
}

/** First line as title (thread + feed cards). */
export function titleFromPost(post) {
  const c = String(post?.content || "").trim();
  if (!c) return "Club discussion";
  const line = c.split("\n")[0];
  return line.length > 100 ? `${line.slice(0, 100)}…` : line;
}

/** Body after first newline (pairs with titleFromPost). */
export function bodyAfterFirstLine(post) {
  const c = String(post?.content || "").trim();
  if (!c) return "";
  const i = c.indexOf("\n");
  if (i === -1) return "";
  return c.slice(i + 1).trim();
}

/**
 * Map club comment POST errors (rate limit / duplicate) to UI copy. Other errors pass through message.
 * @param {Error & { status?: number, data?: unknown }} err
 */
export function messageForClubCommentSubmitError(err, fallback = "Could not post comment.") {
  const status = err?.status;
  const rawApi =
    err?.data && typeof err.data === "object" && err.data !== null
      ? String(err.data.error ?? "").trim()
      : "";
  const apiLower = rawApi.toLowerCase();
  const msg = String(err?.message || "").trim();
  const msgLower = msg.toLowerCase();

  if (status === 429 || apiLower === "too many requests" || msgLower === "too many requests") {
    return "You're commenting too fast. Please wait a moment.";
  }
  if (status === 400 && apiLower === "duplicate comment") {
    return "That comment was already submitted.";
  }
  if (msgLower === "duplicate comment") {
    return "That comment was already submitted.";
  }
  return msg || fallback;
}

/**
 * @param {Error & { status?: number, data?: unknown }} err
 */
export function messageForClubCommentLikeError(err, fallback = "Could not update comment like.") {
  const status = err?.status;
  const rawApi =
    err?.data && typeof err.data === "object" && err.data !== null
      ? String(err.data.error ?? "").trim()
      : "";
  const apiLower = rawApi.toLowerCase();
  const msg = String(err?.message || "").trim();
  const msgLower = msg.toLowerCase();

  if (status === 429 || apiLower === "too many requests" || msgLower === "too many requests") {
    return "You're liking comments too fast. Please wait a moment.";
  }
  return msg || fallback;
}

/** Plain text for in-thread search (username + body; matches ClubCommentNode parsing). */
export function clubCommentPlainTextForSearch(comment) {
  if (!comment || typeof comment !== "object") return "";
  const username = String(comment.username ?? comment.user_name ?? "").trim();
  if (asBool(comment.is_deleted ?? comment.isDeleted)) {
    return `${username} [deleted]`.replace(/\s+/g, " ").trim();
  }
  const readVisibleBody = (value) => {
    const coerced = typeof value === "string" ? value : coerceClubCommentContentToString(value);
    const s = String(coerced || "").trim();
    if (!s) return "";
    if (!s.startsWith("{")) return s;
    try {
      const o = JSON.parse(s);
      if (o && typeof o === "object") {
        if (o.text != null) return String(o.text);
        return "";
      }
    } catch {
      return "";
    }
    return s;
  };
  const raw = comment.content ?? comment.text ?? comment.comment_text ?? "";
  const body = readVisibleBody(raw);
  return `${username} ${body}`.replace(/\s+/g, " ").trim();
}

export function clubCommentMatchesSearch(comment, rawQuery) {
  const q = String(rawQuery || "").trim().toLowerCase();
  if (!q) return true;
  return clubCommentPlainTextForSearch(comment).toLowerCase().includes(q);
}

/**
 * Main user-facing search contract: only body/username matches are allowed.
 * If backend provenance flags are present, trust them; otherwise infer locally.
 */
export function isClubCommentUserFacingSearchMatch(comment, rawQuery) {
  if (!comment || typeof comment !== "object") return false;
  if (comment.matched_in_body === true || comment.matched_in_username === true) return true;
  if (comment.matched_in_body === false && comment.matched_in_username === false) return false;
  const q = String(rawQuery || "").trim().toLowerCase();
  if (!q) return true;
  const username = String(comment.username ?? comment.user_name ?? "").trim().toLowerCase();
  if (username.includes(q)) return true;
  const raw = comment.content ?? comment.text ?? comment.comment_text ?? "";
  const coerced = typeof raw === "string" ? raw : coerceClubCommentContentToString(raw);
  const s = String(coerced || "").trim();
  if (!s) return false;
  let body = s;
  if (s.startsWith("{")) {
    try {
      const o = JSON.parse(s);
      if (o && typeof o === "object") {
        body = o.text != null ? String(o.text) : "";
      }
    } catch {
      body = "";
    }
  }
  return String(body || "").toLowerCase().includes(q);
}

function collapseWs(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

/**
 * Body-match search snippet: anchor the window so the match sits near the *start* of the visible
 * text (small lead-in, most of the budget after the match). Avoids unrelated early words dominating.
 */
function clipCenterAroundMatch(text, index, matchLen, maxLen) {
  const s = collapseWs(text);
  if (!s) return "";
  if (!Number.isFinite(maxLen) || maxLen < 24) return s;
  const i = Math.max(0, Math.min(Number(index) || 0, s.length - 1));
  const safeMatchLen = Math.max(1, Number(matchLen) || 1);
  // Tight lead-in: ~6–14 chars before match (not a large % of maxLen).
  const leadIn = Math.min(14, Math.max(6, Math.floor(maxLen * 0.07)));
  let start = Math.max(0, i - leadIn);
  let end = Math.min(s.length, start + maxLen);
  // If the match would extend past end (rare), shift right only as much as needed.
  if (i + safeMatchLen > end) {
    start = Math.max(0, i + safeMatchLen - maxLen);
    end = Math.min(s.length, start + maxLen);
  }
  // Prefer breaking lead-in at a word boundary when we use an ellipsis (cleaner "...think me").
  if (start > 0) {
    const slice = s.slice(start, i);
    const lastSpace = slice.lastIndexOf(" ");
    if (lastSpace > 0 && lastSpace < slice.length - 1) {
      const bump = lastSpace + 1;
      if (i - (start + bump) <= leadIn + 8) {
        start += bump;
        end = Math.min(s.length, start + maxLen);
        if (i + safeMatchLen > end) {
          start = Math.max(0, i + safeMatchLen - maxLen);
          end = Math.min(s.length, start + maxLen);
        }
      }
    }
  }
  const prefix = start > 0 ? "…" : "";
  const suffix = end < s.length ? "…" : "";
  return `${prefix}${s.slice(start, end)}${suffix}`;
}

function clipHead(text, maxLen = 168) {
  const s = collapseWs(text);
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
}

/**
 * Honest preview for search results from the matched comment row.
 * - Prefers showing the body excerpt around the query match when available.
 * - If only username matches, keeps body preview and flags that the username matched.
 */
export function buildClubCommentSearchPreview(comment, rawQuery, maxLen = 168) {
  if (!comment || typeof comment !== "object") {
    return { excerpt: "", matchedIn: "none", username: "", body: "", highlightedExcerpt: null };
  }
  const username = collapseWs(comment.username ?? comment.user_name ?? "");
  if (asBool(comment.is_deleted ?? comment.isDeleted)) {
    return {
      excerpt: "[deleted]",
      matchedIn: "none",
      username,
      body: "",
      highlightedExcerpt: null,
    };
  }
  const raw = comment.content ?? comment.text ?? comment.comment_text ?? "";
  const coerced = typeof raw === "string" ? raw : coerceClubCommentContentToString(raw);
  const s = String(coerced || "").trim();
  let body = s;
  if (s.startsWith("{")) {
    try {
      const o = JSON.parse(s);
      if (o && typeof o === "object") {
        body = o.text != null ? String(o.text) : "";
      }
    } catch {
      /* Align with backend: never treat broken JSON as visible body (avoids URL/metadata false positives). */
      body = "";
    }
  }
  const bodyNorm = collapseWs(body);
  const rawNorm = collapseWs(s);
  const q = collapseWs(rawQuery).toLowerCase();
  if (!q) {
    return {
      excerpt: clipHead(bodyNorm || username, maxLen),
      matchedIn: "none",
      username,
      body: bodyNorm,
      highlightedExcerpt: null,
    };
  }
  const bodyIdx = bodyNorm.toLowerCase().indexOf(q);
  if (bodyIdx >= 0) {
    const excerpt = clipCenterAroundMatch(bodyNorm, bodyIdx, q.length, maxLen);
    const inExcerptIdx = excerpt.toLowerCase().indexOf(q);
    let highlightedExcerpt = null;
    if (inExcerptIdx >= 0) {
      highlightedExcerpt = {
        before: excerpt.slice(0, inExcerptIdx),
        match: excerpt.slice(inExcerptIdx, inExcerptIdx + q.length),
        after: excerpt.slice(inExcerptIdx + q.length),
      };
    }
    return {
      excerpt,
      matchedIn: "body",
      username,
      body: bodyNorm,
      highlightedExcerpt,
    };
  }
  const nameIdx = username.toLowerCase().indexOf(q);
  if (nameIdx >= 0 || comment.matched_in_username === true) {
    return {
      excerpt: clipHead(bodyNorm || username, maxLen),
      matchedIn: "username",
      username,
      body: bodyNorm,
      highlightedExcerpt: null,
    };
  }
  const rawIdx = rawNorm.toLowerCase().indexOf(q);
  if (rawIdx >= 0) {
    return {
      excerpt: clipHead(bodyNorm || "", maxLen),
      matchedIn: "raw",
      username,
      body: bodyNorm,
      highlightedExcerpt: null,
    };
  }
  return {
    excerpt: clipHead(bodyNorm || "", maxLen),
    matchedIn: "none",
    username,
    body: bodyNorm,
    highlightedExcerpt: null,
  };
}

/**
 * If the API row is missing author fields (e.g. transient insert response), fill from the
 * authenticated viewer only when this comment belongs to them — never for other users' rows.
 */
export function mergeViewerOntoOwnClubCommentIfNeeded(row, viewerId, viewerIdentity) {
  if (!row || typeof row !== "object" || viewerId == null) return row;
  if (!viewerIdentity || typeof viewerIdentity !== "object") return row;
  const uid = row.user_id != null ? Number(row.user_id) : NaN;
  const vid = Number(viewerId);
  if (!Number.isFinite(uid) || !Number.isFinite(vid) || uid !== vid) return row;

  const vName = viewerIdentity.username ?? viewerIdentity.userName ?? null;
  const vPic =
    viewerIdentity.profile_picture ?? viewerIdentity.profilePicture ?? viewerIdentity.avatar_url ?? null;

  const next = { ...row };
  if ((!next.username || next.username === "Member") && vName != null && String(vName).trim() !== "") {
    next.username = String(vName).trim();
  }
  if (!next.profile_picture && vPic != null && String(vPic).trim() !== "") {
    next.profile_picture = String(vPic).trim();
  }
  return next;
}

/** Numeric comment id for threading/sort (prefers `id`, then `comment_id`). */
export function commentRowEffectiveId(c) {
  const raw = c?.id != null && c?.id !== "" ? c.id : c?.comment_id;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Parent id for tree threading only: null for top-level (incl. legacy `0`, invalid, self-loop).
 * Matches normalizeClubCommentRow parent rules.
 */
export function rowParentCommentIdForThread(c) {
  const self = commentRowEffectiveId(c);
  const parentRaw =
    c?.parent_comment_id ??
    c?.parentCommentId ??
    c?.parent_id ??
    c?.parentId ??
    null;
  if (parentRaw == null || parentRaw === "") return null;
  const n = Number(parentRaw);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (self != null && n === self) return null;
  return n;
}

function parseCommentTimestampToMs(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (raw instanceof Date) {
    const x = raw.getTime();
    return Number.isFinite(x) ? x : null;
  }
  const s = String(raw).trim();
  if (!s) return null;
  let t = Date.parse(s);
  if (Number.isFinite(t)) return t;
  // Some SQL/JSON paths use "YYYY-MM-DD HH:mm:ss" without "T"
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) {
    t = Date.parse(s.replace(" ", "T"));
    if (Number.isFinite(t)) return t;
  }
  return null;
}

export function normalizeClubCommentRow(c) {
  if (!c || typeof c !== "object") return c;
  const likeCountRaw = c.like_count ?? c.likeCount ?? 0;
  const like_count = Number.isFinite(Number(likeCountRaw)) ? Number(likeCountRaw) : 0;
  const liked_by_viewer = asBool(c.liked_by_viewer ?? c.likedByViewer);
  const is_deleted = asBool(c.is_deleted ?? c.isDeleted);
  const is_edited_flag = asBool(c.is_edited ?? c.isEdited);
  const updated_at_val = c.updated_at ?? c.updatedAt ?? null;
  let edited_at = c.edited_at ?? c.editedAt ?? null;
  if (!is_deleted && is_edited_flag && edited_at == null && updated_at_val != null) {
    edited_at = updated_at_val;
  }
  const uidRaw = c.user_id ?? c.userId;
  const user_id = uidRaw == null || uidRaw === "" ? null : Number(uidRaw);
  const user_id_norm = Number.isFinite(user_id) ? user_id : null;
  const postRaw = c.post_id ?? c.postId;
  const postNum = postRaw == null || postRaw === "" ? NaN : Number(postRaw);
  const post_id = Number.isFinite(postNum) ? postNum : postRaw ?? null;
  const idRaw = c.id != null && c.id !== "" ? c.id : c.comment_id;
  const idNum = idRaw == null || idRaw === "" ? NaN : Number(idRaw);
  const id = Number.isFinite(idNum) ? idNum : idRaw;

  const parentRaw =
    c.parent_comment_id ??
    c.parentCommentId ??
    c.parent_id ??
    c.parentId ??
    null;
  const parentIdNum = parentRaw == null || parentRaw === "" ? null : Number(parentRaw);
  let parent_comment_id = Number.isFinite(parentIdNum) ? parentIdNum : null;
  if (parent_comment_id === 0) parent_comment_id = null;
  if (parent_comment_id != null && Number.isFinite(idNum) && parent_comment_id === idNum) {
    parent_comment_id = null;
  }

  const rawBody = c.content ?? c.text ?? c.comment_text;
  const content = is_deleted ? "" : coerceClubCommentContentToString(rawBody);

  return {
    ...c,
    id,
    post_id,
    content,
    created_at: c.created_at ?? c.createdAt,
    updated_at: updated_at_val,
    username: c.username ?? c.user_name ?? c.userName ?? "Member",
    profile_picture: c.profile_picture ?? c.profilePicture ?? null,
    parent_comment_id,
    like_count,
    liked_by_viewer,
    is_deleted,
    is_edited: is_edited_flag,
    edited_at,
    user_id: user_id_norm,
  };
}

/**
 * Merge a PATCH/DELETE API row into existing comment state without dropping fields the list UI needs.
 */
export function mergeClubCommentFromServer(prev, raw) {
  const n = normalizeClubCommentRow(raw);
  if (!n || n.id == null) return prev;
  const base = prev && typeof prev === "object" ? prev : {};
  const mergedParent = (() => {
    let p = n.parent_comment_id != null ? n.parent_comment_id : base.parent_comment_id ?? null;
    if (p === 0 || !Number.isFinite(Number(p)) || Number(p) <= 0) return null;
    const nid = Number(n.id);
    if (Number.isFinite(nid) && Number(p) === nid) return null;
    return p;
  })();

  return {
    ...base,
    ...n,
    id: n.id,
    comment_id: n.comment_id != null ? n.comment_id : n.id ?? base.comment_id,
    post_id: n.post_id != null ? n.post_id : base.post_id,
    parent_comment_id: mergedParent,
    content: n.content,
    is_deleted: asBool(n.is_deleted),
    is_edited: asBool(n.is_edited),
    edited_at: n.edited_at ?? base.edited_at ?? null,
    updated_at: n.updated_at ?? base.updated_at ?? null,
    like_count: Number.isFinite(Number(n.like_count)) ? Number(n.like_count) : Number(base.like_count) || 0,
    liked_by_viewer: typeof n.liked_by_viewer === "boolean" ? n.liked_by_viewer : Boolean(base.liked_by_viewer),
    username: n.username ?? base.username ?? "Member",
    profile_picture: n.profile_picture ?? base.profile_picture ?? null,
    user_id: n.user_id != null ? n.user_id : base.user_id ?? null,
    attachments: Array.isArray(n.attachments) ? n.attachments : base.attachments ?? [],
  };
}

/**
 * Thread list sort (ArtHouse artwork + Clubs post/thread via CommentThreadList).
 * Keep in sync with backend/utils/clubCommentReadShared.js — compareSiblingCommentsForSort.
 */
export function normalizeCommentListSort(sort) {
  const s = String(sort == null ? "new" : sort)
    .toLowerCase()
    .trim();
  if (s === "old" || s === "top" || s === "new") return s;
  return "new";
}

export function commentRowCreatedMs(c) {
  let ms = parseCommentTimestampToMs(c?.created_at ?? c?.createdAt);
  if (ms == null) ms = parseCommentTimestampToMs(c?.updated_at ?? c?.updatedAt);
  return ms ?? 0;
}

/** Numeric id for sort tie-breaks; prefers id then comment_id (matches normalizeClubCommentRow). */
export function commentRowIdNumForSort(c) {
  const raw = c?.id != null && c?.id !== "" ? c.id : c?.comment_id;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export function commentRowLikeCountForSort(c) {
  const n = Number(c?.like_count ?? c?.likeCount);
  return Number.isFinite(n) ? n : 0;
}

export function compareCommentSiblingsForSort(a, b, sortMode) {
  const mode = normalizeCommentListSort(sortMode);
  const ta = commentRowCreatedMs(a);
  const tb = commentRowCreatedMs(b);
  const ida = commentRowIdNumForSort(a);
  const idb = commentRowIdNumForSort(b);
  const la = commentRowLikeCountForSort(a);
  const lb = commentRowLikeCountForSort(b);
  if (mode === "old") {
    if (ta !== tb) return ta - tb;
    return ida - idb;
  }
  if (mode === "top") {
    if (lb !== la) return lb - la;
    if (tb !== ta) return tb - ta;
    return idb - ida;
  }
  if (tb !== ta) return tb - ta;
  return idb - ida;
}

/** Root or orphan tier: reorder by New / Old / Top (nested __children keep list order until re-threaded). */
export function sortCommentRootNodesInPlace(roots, sort) {
  if (!Array.isArray(roots)) return;
  roots.sort((a, b) => compareCommentSiblingsForSort(a, b, sort));
}

/** Same as sortCommentRootNodesInPlace — pre-refactor name used by some call sites. */
export { sortCommentRootNodesInPlace as sortRootNodesInPlace };

/**
 * Dev-only: set `window.__DEBUG_COMMENT_SORT__ = true` then open a thread — logs root-tier rows
 * (structural vs orphan), parsed timestamps, and sort index for new/old/top.
 */
export function logCommentThreadRootSortDiagnostics(flatList, opts = {}) {
  if (typeof window === "undefined" || !window.__DEBUG_COMMENT_SORT__) return;
  if (!Array.isArray(flatList) || flatList.length === 0) return;

  const byId = new Map();
  flatList.forEach((c) => {
    const id = commentRowEffectiveId(c);
    if (id != null) byId.set(id, c);
  });

  const rootTier = [];
  flatList.forEach((c) => {
    const id = commentRowEffectiveId(c);
    if (id == null) return;
    const pid = rowParentCommentIdForThread(c);
    if (pid != null && byId.has(pid)) return;
    const kind = pid == null ? "structural_root" : "orphan_root";
    rootTier.push({ row: c, kind });
  });

  const modes = ["new", "old", "top"];
  const positions = {};
  for (const mode of modes) {
    const sorted = rootTier.map((x) => x.row);
    sortCommentRootNodesInPlace(sorted, mode);
    positions[mode] = new Map();
    sorted.forEach((row, i) => {
      const eid = commentRowEffectiveId(row);
      if (eid != null) positions[mode].set(eid, i + 1);
    });
  }

  const lines = rootTier.map(({ row, kind }) => {
    const eid = commentRowEffectiveId(row);
    const rawCreated = row.created_at ?? row.createdAt;
    const parsed = commentRowCreatedMs(row);
    return {
      raw_id: row.id,
      raw_comment_id: row.comment_id,
      effective_id: eid,
      raw_parent_comment_id: row.parent_comment_id,
      raw_created_at: rawCreated,
      parsed_created_ms: parsed,
      raw_updated_at: row.updated_at ?? row.updatedAt,
      like_count: row.like_count,
      target_type: row.target_type,
      target_id: row.target_id,
      root_tier: kind,
      pos_new: positions.new.get(eid) ?? null,
      pos_old: positions.old.get(eid) ?? null,
      pos_top: positions.top.get(eid) ?? null,
    };
  });

  console.info("[comment root sort diagnostics]", opts);
  console.table(lines);
}

function rowCommentIdNum(c) {
  const n = Number(c?.id ?? c?.comment_id);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Flat comment rows → anchor row plus every descendant (via parent_comment_id), in original list order.
 * Mirrors ClubCommentThreadPage’s branch payload (`[anchor, ...desc]`) for inline “continue thread” on surfaces without a dedicated thread route.
 */
export function sliceCommentBranchByAnchorId(flatList, anchorId) {
  const anchorNum = Number(anchorId);
  if (!Number.isFinite(anchorNum) || !Array.isArray(flatList) || flatList.length === 0) {
    return flatList;
  }
  const hasAnchor = flatList.some((c) => rowCommentIdNum(c) === anchorNum);
  if (!hasAnchor) return flatList;

  const included = new Set([anchorNum]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const c of flatList) {
      const cid = rowCommentIdNum(c);
      if (!Number.isFinite(cid) || included.has(cid)) continue;
      let pid = Number(c?.parent_comment_id ?? c?.parentCommentId);
      if (pid === cid) pid = NaN;
      if (Number.isFinite(pid) && included.has(pid)) {
        included.add(cid);
        changed = true;
      }
    }
  }

  const indexById = new Map();
  flatList.forEach((c, i) => {
    const cid = rowCommentIdNum(c);
    if (Number.isFinite(cid) && !indexById.has(cid)) indexById.set(cid, i);
  });

  return flatList
    .filter((c) => included.has(rowCommentIdNum(c)))
    .sort((a, b) => (indexById.get(rowCommentIdNum(a)) ?? 0) - (indexById.get(rowCommentIdNum(b)) ?? 0));
}

export function getOptimisticPostLikeState({ liked, likeCount }) {
  return buildOptimisticLikeState({
    liked_by_viewer: liked,
    like_count: likeCount,
  });
}

export function getPostLikeStateFromToggleResponse(response, optimisticState) {
  const canonical = reconcileLikeStateFromResponse(response, optimisticState);

  return {
    nextLiked: canonical.liked_by_viewer,
    nextCount: canonical.like_count,
  };
}

/** Resolve club feed / R2 / API-relative media URLs (shared by feed + thread). */
export function resolveClubMediaUrl(url) {
  if (!url) return null;
  let s = String(url).trim();
  if (!s) return null;
  if (s.startsWith("undefined/")) s = s.replace(/^undefined\/+/, "");
  if (/^https?:\/\//i.test(s)) return s;
  if (/^\/\//.test(s)) {
    if (typeof window !== "undefined" && window.location?.protocol) {
      return `${window.location.protocol}${s}`;
    }
    return `https:${s}`;
  }
  if (/^(data:|blob:)/i.test(s)) return s;

  const r2Base = (process.env.REACT_APP_R2_PUBLIC_BASE || "").trim();
  if (r2Base && /^clubs\//i.test(s)) {
    return `${r2Base.replace(/\/+$/, "")}/${s.replace(/^\/+/, "")}`;
  }

  const rooted = s.startsWith("/") ? s : `/${s}`;
  const apiBase = (process.env.REACT_APP_API_BASE || "").trim();
  if (apiBase) return `${apiBase.replace(/\/+$/, "")}${rooted}`;
  if (typeof window !== "undefined") {
    const { protocol, hostname, port } = window.location;
    if (
      (hostname === "localhost" || hostname === "127.0.0.1") &&
      (port === "3000" || port === "5173")
    ) {
      return `${protocol}//${hostname}:5000${rooted}`;
    }
  }
  return rooted;
}

/** Full timestamp for thread / detail views. */
export function formatClubThreadDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

const MS_SECOND = 1000;
const MS_MINUTE = 60 * MS_SECOND;
const MS_HOUR = 60 * MS_MINUTE;
const MS_DAY = 24 * MS_HOUR;

/**
 * Reddit-style relative label for comment rows; `title` carries the full local datetime for hover.
 * @returns {{ label: string, title: string, dateTimeAttr: string }}
 */
export function formatClubCommentRelativeMeta(iso) {
  const title = formatClubThreadDateTime(iso);
  if (!iso) return { label: "", title: "", dateTimeAttr: "" };
  let d;
  try {
    d = new Date(iso);
  } catch {
    return { label: title, title, dateTimeAttr: "" };
  }
  const t = d.getTime();
  if (Number.isNaN(t)) return { label: title, title, dateTimeAttr: "" };

  let dateTimeAttr = "";
  try {
    dateTimeAttr = d.toISOString();
  } catch {
    dateTimeAttr = "";
  }

  const now = Date.now();
  const diff = now - t;
  if (diff < 0) return { label: title, title, dateTimeAttr };

  if (diff < 45 * MS_SECOND) return { label: "just now", title, dateTimeAttr };
  if (diff < MS_MINUTE) return { label: "1m ago", title, dateTimeAttr };
  if (diff < MS_HOUR) {
    const m = Math.floor(diff / MS_MINUTE);
    return { label: `${m}m ago`, title, dateTimeAttr };
  }
  if (diff < MS_DAY) {
    const h = Math.floor(diff / MS_HOUR);
    return { label: `${h}h ago`, title, dateTimeAttr };
  }
  if (diff < 7 * MS_DAY) {
    const days = Math.floor(diff / MS_DAY);
    return { label: `${days}d ago`, title, dateTimeAttr };
  }

  const yNow = new Date().getFullYear();
  const y = d.getFullYear();
  const shortLabel = d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    ...(y !== yNow ? { year: "numeric" } : {}),
  });
  return { label: shortLabel, title, dateTimeAttr };
}

/** Single visible character for avatar fallbacks. */
export function clubAvatarInitial(label) {
  const s = String(label || "U").trim();
  if (!s) return "U";
  return s.slice(0, 1).toUpperCase();
}
